import React, { useState, useEffect, useRef } from 'react';
import { useNetworkInfo } from '../hooks/useNetworkInfo';

/**
 * Lazy loading video s podporou Network Information API a progresívnym načítaním
 * Najprv zobrazí rozmazaný/pixelovaný video, ktorý sa postupne rozkreslí
 */
const LazyVideo = ({ 
  src, 
  poster = null,
  lowQualitySrc = null,
  className = '',
  autoPlay = false,
  loop = false,
  muted = true,
  playsInline = true,
  enableProgressive = true, // Povoliť progresívne načítanie s blur efektom
  eager = false, // Ak true, načíta video okamžite bez čakania na IntersectionObserver
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(eager); // Ak je eager, začneme načítavať okamžite
  const [shouldAutoplay, setShouldAutoplay] = useState(eager && autoPlay); // Ak je eager a autoPlay, spustíme okamžite
  // Začneme s blur len ak je pomalé pripojenie, inak 0
  const [blurIntensity, setBlurIntensity] = useState(0);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const { isSlowConnection, saveData, isSupported } = useNetworkInfo();

  // Nastavíme blur hneď keď sa začne načítavať (pri pomalých pripojeniach)
  useEffect(() => {
    if (shouldLoad && enableProgressive && (isSlowConnection || saveData)) {
      setBlurIntensity(20);
    }
  }, [shouldLoad, enableProgressive, isSlowConnection, saveData]);

  useEffect(() => {
    // Ak je eager, nepotrebujeme IntersectionObserver - video sa načíta okamžite
    if (eager) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            
            // Na pomalých pripojeniach alebo save-data móde:
            // - Oneskoríme načítanie videa
            // - Môžeme nepustiť autoplay
            if (isSlowConnection || saveData) {
              // Na pomalých pripojeniach oneskoríme načítanie o 500ms
              setTimeout(() => {
                setShouldLoad(true);
                // Autoplay len ak nie je pomalé pripojenie a nie je save-data
                if (autoPlay && !saveData) {
                  setShouldAutoplay(true);
                }
              }, 500);
            } else {
              setShouldLoad(true);
              if (autoPlay) {
                setShouldAutoplay(true);
              }
            }
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px' // Začneme načítavať 100px pred tým, ako sa video objaví
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isSlowConnection, saveData, autoPlay, eager]);

  // Rozhodnutie, ktoré video načítať
  const videoSrc = shouldLoad 
    ? (isSlowConnection && lowQualitySrc ? lowQualitySrc : src)
    : null;

  // Ak je save-data aktívny, zobrazíme len poster
  const showOnlyPoster = saveData && poster;

  // iOS specific handling - force reload on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && videoRef.current) {
        // Force reload video on iOS when page becomes visible
        const video = videoRef.current;
        if (video.paused && autoPlay) {
          video.load();
          video.play().catch((error) => {
            console.log('Video play failed:', error);
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoPlay]);

  // Force video to load and play on mount for iOS
  useEffect(() => {
    if (videoRef.current && shouldAutoplay && !showOnlyPoster) {
      const video = videoRef.current;
      video.load();
      video.play().catch((error) => {
        console.log('Autoplay bol blokovaný:', error);
      });
    }
  }, [shouldAutoplay, showOnlyPoster]);

  const handleVideoLoaded = () => {
    setIsLoaded(true);
    // Postupne znižujeme blur efekt len pri pomalých pripojeniach
    // Pri rýchlych pripojeniach sa video načíta okamžite bez blur efektu
    const shouldUseProgressive = enableProgressive && (isSlowConnection || saveData);
    
    if (shouldUseProgressive) {
      // Nastavíme počiatočný blur pred začatím animácie
      setBlurIntensity(20);
      const blurSteps = [15, 10, 5, 2, 0];
      let stepIndex = 0;
      const blurInterval = setInterval(() => {
        if (stepIndex < blurSteps.length) {
          setBlurIntensity(blurSteps[stepIndex]);
          stepIndex++;
        } else {
          clearInterval(blurInterval);
        }
      }, 100); // Každých 100ms znížime blur
    } else {
      // Pri rýchlych pripojeniach okamžite odstránime blur
      setBlurIntensity(0);
    }
  };

  if (showOnlyPoster) {
    // Na save-data móde zobrazíme len poster obrázok
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        <img
          src={poster}
          alt="Video poster"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="text-white text-center">
            <svg className="w-16 h-16 mx-auto mb-2 opacity-75" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
            <p className="text-sm">Video je dostupné po vypnutí režimu úspory dát</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Video element s progresívnym blur efektom len pri pomalých pripojeniach */}
      {videoSrc && (
        <video
          ref={videoRef}
          src={videoSrc}
          poster={poster}
          className={className}
          style={{
            filter: (enableProgressive && (isSlowConnection || saveData)) ? `blur(${blurIntensity}px)` : 'none',
            transform: (enableProgressive && (isSlowConnection || saveData) && blurIntensity > 0) ? 'scale(1.05)' : 'scale(1)',
            transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
          }}
          autoPlay={shouldAutoplay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          preload="auto"
          onLoadedData={handleVideoLoaded}
          onError={(e) => {
            console.error('Video error:', e);
            // Retry loading on error
            if (videoRef.current) {
              setTimeout(() => {
                videoRef.current?.load();
              }, 1000);
            }
          }}
          {...props}
        >
          <source src={videoSrc} type="video/mp4" />
          Váš prehliadač nepodporuje video tag.
        </video>
      )}

      {/* Placeholder ak video ešte nie je načítané - s blur efektom len pri pomalých pripojeniach */}
      {!videoSrc && poster && (
        <img
          src={poster}
          alt="Video poster"
          className="w-full h-full object-cover"
          style={{
            filter: (enableProgressive && (isSlowConnection || saveData)) ? `blur(${blurIntensity}px)` : 'none',
            transform: (enableProgressive && (isSlowConnection || saveData) && blurIntensity > 0) ? 'scale(1.05)' : 'scale(1)',
            transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
          }}
        />
      )}
    </div>
  );
};

export default LazyVideo;
